import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import {
  Grid, Box, Paper, Typography, Snackbar, Alert, Chip, 
  FormControl, InputLabel, Select, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, IconButton, Tooltip, Divider
} from '@mui/material';
import { 
  Search as SearchIcon, 
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  Visibility as ViewIcon,
  Print as PrintIcon
} from "@mui/icons-material";
import { ExecuteApiToPost, ExecutePostWithParams, GetDataFromApiWithParams } from "../../../../../services/api/ExecuteApiRequests";
import './AdvancePaymentForm.css';

// TabPanel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`advance-payment-tabpanel-${index}`}
      aria-labelledby={`advance-payment-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const AdvancePaymentForm = () => {
  // Refs for printing
  const tableRef = useRef(null);
  
  // Tab state
  const [tabValue, setTabValue] = useState(0);
  
  // Initialize agreements as empty array
  const [agreements, setAgreements] = useState([]);
  const [filteredAgreements, setFilteredAgreements] = useState([]);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  
  // Agreement type filter
  const [selectedAgreementType, setSelectedAgreementType] = useState("all");
  const [isLoadingAgreements, setIsLoadingAgreements] = useState(false);
  
  // Form data for registration
  const [formData, setFormData] = useState({
    agreementId: "",
    paymentDate: "",
    paymentAmount: "",
    paymentMode: "",
    paidThrough: "",
    bankName: "",
    referenceNo: "",
    remarks: "",
    status: "Requested",
  });
  
  // State for advance payment list
  const [advancePayments, setAdvancePayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [dateRange, setDateRange] = useState({
    fromDate: "",
    toDate: ""
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  
  // Pagination for list tab
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  
  // Toast states
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [isLoading, setIsLoading] = useState(false);

  // Agreement type options
  const agreementTypes = [
    { id: "all", label: "All Agreement Types" },
    { id: "Shed Rent", label: "Shed Rent" },
    { id: "Land Leased", label: "Land Leased" },
    { id: "residential", label: "Residential" },
    { id: "commercial", label: "Commercial" }
  ];

  // Paid through options
  const paidThroughOptions = [
    { value: "Cash", label: "Cash" },
    { value: "Bank transfer", label: "Bank Transfer" },
    { value: "Cheque", label: "Cheque" }
  ];

  // Status options for filter
  const statusOptions = [
    { value: "all", label: "All Status" },
    { value: "Paid", label: "Paid" },
    { value: "Unpaid", label: "Unpaid" },
    { value: "Requested", label: "Requested" }
  ];

  // Toast functions
  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  const handleToastClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastOpen(false);
  };

  const showSuccessToast = (message) => showToast(message, "success");
  const showErrorToast = (message) => showToast(message, "error");
  const showWarningToast = (message) => showToast(message, "warning");
  const showInfoToast = (message) => showToast(message, "info");

  // Reset form function
  const resetForm = () => {
    setFormData({
      agreementId: "",
      paymentDate: "",
      paymentAmount: "",
      paymentMode: "",
      paidThrough: "",
      bankName: "",
      referenceNo: "",
      remarks: "",
      status: "Requested",
    });
    setSelectedAgreement(null);
  };

  // Function to fetch agreements based on selected type
  const fetchAgreementsByType = async (type) => {
    try {
      setIsLoadingAgreements(true);
      
      console.log(`Fetching agreements for type: ${type}`);
      
      const agreData = await GetDataFromApiWithParams(
        "agreements/getagreementbytype", 
        { agreement_type: type }
      );
      
      console.log(`AGREEMENTS for type ${type}:`, agreData);
      
      if (agreData && Array.isArray(agreData)) {
        const uniqueAgreData = [...new Map(agreData.map(item => [item.id, item])).values()];
        setAgreements(uniqueAgreData);
        setFilteredAgreements(uniqueAgreData);
      } else {
        setAgreements([]);
        setFilteredAgreements([]);
      }
    } catch (error) {
      console.error("Error fetching agreements:", error);
      setAgreements([]);
      setFilteredAgreements([]);
      showErrorToast("Failed to fetch agreements");
    } finally {
      setIsLoadingAgreements(false);
    }
  };

  // Handle agreement type change
  const handleAgreementTypeChange = async (e) => {
    const type = e.target.value;
    console.log("AGREEMENT TYPE SELECTED:", type);
    setSelectedAgreementType(type);
    
    setFormData(prev => ({
      ...prev,
      agreementId: "",
      paymentAmount: "",
      paymentMode: "",
      paidThrough: "",
      bankName: ""
    }));
    setSelectedAgreement(null);
    
    await fetchAgreementsByType(type);
  };

  // Load agreements on component mount with default type
  useEffect(() => {
    fetchAgreementsByType(selectedAgreementType);
  }, []);

  // Format payment mode for display
  const formatPaymentMode = (mode) => {
    if (!mode) return "";
    return mode.charAt(0).toUpperCase() + mode.slice(1).toLowerCase();
  };

  // Get agreement type color for chip display
  const getAgreementTypeColor = (type) => {
    switch(type?.toLowerCase()) {
      case 'shed rent':
      case 'shed land':
        return 'primary';
      case 'land leased':
        return 'secondary';
      case 'residential':
        return 'success';
      case 'commercial':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Format agreement type for display
  const formatAgreementType = (type) => {
    if (!type) return '';
    if (type.toLowerCase().includes('shed')) return 'Shed Rent';
    if (type.toLowerCase().includes('land')) return 'Land Leased';
    if (type.toLowerCase().includes('residential')) return 'Residential';
    if (type.toLowerCase().includes('commercial')) return 'Commercial';
    return type;
  };

  // Check if bank field should be enabled
  const isBankFieldEnabled = () => {
    return formData.paidThrough === "Bank transfer" || formData.paidThrough === "Cheque";
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "agreementId") {
      console.log("Selected agreement ID:", value);
      
      const agreement = filteredAgreements.find(a => a.id === value || a.agreement_id === value);
      console.log("Found agreement:", agreement);
      
      setSelectedAgreement(agreement);
      
      let paymentAmount = "";
      if (agreement) {
        if (agreement.advance_payment && parseFloat(agreement.advance_payment) > 0) {
          paymentAmount = agreement.advance_payment;
        } 
        else if (agreement.down_payment && parseFloat(agreement.down_payment) > 0) {
          paymentAmount = agreement.down_payment;
        }
        else if (agreement.security && parseFloat(agreement.security) > 0) {
          paymentAmount = agreement.security;
        }
      }
      
      const formattedPaymentMode = formatPaymentMode(agreement?.payment_mode);
      
      setFormData(prev => ({
        ...prev,
        agreementId: value,
        paymentAmount: paymentAmount,
        paymentMode: formattedPaymentMode,
        paidThrough: "",
        bankName: ""
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handle date range change
  const handleDateRangeChange = (e) => {
    const { name, value } = e.target;
    setDateRange(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle status filter change
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  // MODIFIED: Fetch advance payments based on filters (now allows null dates)
  const fetchAdvancePayments = async () => {
    // Remove the validation that required both dates
    // if (!dateRange.fromDate || !dateRange.toDate) {
    //   showErrorToast("Please select both From and To dates");
    //   return;
    // }

    try {
      setIsLoadingPayments(true);
      showInfoToast("Fetching advance payments...");
      
      const params = {
        from_date: dateRange.fromDate || null,  // Send null if empty
        to_date: dateRange.toDate || null,      // Send null if empty
        status: statusFilter !== "all" ? statusFilter : null
      };
      
      console.log("Fetching payments with params:", params);
      const result = await GetDataFromApiWithParams("finances/advancepayment/list", params);
      
      console.log("Advance payments response:", result);
      
      if (result && Array.isArray(result)) {
        setAdvancePayments(result);
        setFilteredPayments(result);
        setTotalRecords(result.length);
        setPage(0);
        
        if (result.length > 0) {
          showSuccessToast(`Found ${result.length} advance payment(s)`);
        } else {
          showInfoToast("No advance payments found for the selected criteria");
        }
      } else {
        setAdvancePayments([]);
        setFilteredPayments([]);
        setTotalRecords(0);
        showInfoToast("No advance payments found");
      }
    } catch (error) {
      console.error("Error fetching advance payments:", error);
      showErrorToast("Failed to fetch advance payments");
      setAdvancePayments([]);
      setFilteredPayments([]);
      setTotalRecords(0);
    } finally {
      setIsLoadingPayments(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setDateRange({
      fromDate: "",
      toDate: ""
    });
    setStatusFilter("all");
    setAdvancePayments([]);
    setFilteredPayments([]);
    setTotalRecords(0);
    showInfoToast("Filters cleared");
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Format currency
  const formatCurrency = (amount, mode) => {
    const num = parseFloat(amount) || 0;
    const formatted = num.toFixed(2);
    return mode?.toUpperCase() === "USD" ? `$${formatted}` : `${formatted} ETB`;
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get status chip color
  const getStatusColor = (status) => {
    switch(status?.toLowerCase()) {
      case 'paid':
        return 'success';
      case 'unpaid':
        return 'error';
      case 'requested':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Print function for tab 2
  const handlePrint = () => {
    if (!tableRef.current || filteredPayments.length === 0) {
      showErrorToast("No data to print");
      return;
    }

    try {
      showInfoToast("Preparing print...");
      
      // Create a new window for printing
      const printWindow = window.open('', '_blank');
      
      // Get filter information
      const fromDateFormatted = dateRange.fromDate ? new Date(dateRange.fromDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Not specified';
      const toDateFormatted = dateRange.toDate ? new Date(dateRange.toDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Not specified';
      const statusText = statusFilter !== "all" ? statusFilter : "All";
      
      // Get current date for print header
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      // Calculate totals
      const totalAmount = filteredPayments.reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0).toFixed(2);
      const paidCount = filteredPayments.filter(p => p.status?.toLowerCase() === 'approved').length;
      const unpaidCount = filteredPayments.filter(p => p.status?.toLowerCase() === 'unpaid' || p.status?.toLowerCase() === 'requested').length;
      const requestedCount = filteredPayments.filter(p => p.status?.toLowerCase() === 'requested').length;

      // Get the table HTML
      const tableHtml = tableRef.current.cloneNode(true);
      
      // Remove pagination and any extra elements from the cloned table
      const paginationElements = tableHtml.querySelectorAll('.MuiTablePagination-root');
      paginationElements.forEach(el => el.remove());

      // Write content to print window
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Advance Payment List</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
              padding-bottom: 10px;
              border-bottom: 2px solid #1976d2;
            }
            .header h2 {
              color: #1976d2;
              margin: 0 0 5px 0;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .filter-info {
              background-color: #f5f5f5;
              padding: 10px;
              margin-bottom: 20px;
              border-radius: 5px;
            }
            .filter-info table {
              width: 100%;
              border-collapse: collapse;
            }
            .filter-info td {
              padding: 5px;
            }
            .summary {
              background-color: #e3f2fd;
              padding: 10px;
              margin: 20px 0;
              border-radius: 5px;
            }
            .summary table {
              width: 50%;
              margin: 0 auto;
              border-collapse: collapse;
            }
            .summary td {
              padding: 5px;
            }
            .summary .label {
              font-weight: bold;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 20px 0;
              font-size: 12px;
            }
            th {
              background-color: #1976d2;
              color: white;
              padding: 8px;
              text-align: left;
              font-size: 12px;
            }
            td {
              padding: 6px;
              border-bottom: 1px solid #ddd;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .status-paid {
              background-color: #4caf50;
              color: white;
              padding: 3px 8px;
              border-radius: 3px;
              display: inline-block;
              font-size: 11px;
            }
            .status-unpaid {
              background-color: #f44336;
              color: white;
              padding: 3px 8px;
              border-radius: 3px;
              display: inline-block;
              font-size: 11px;
            }
            .status-requested {
              background-color: #ff9800;
              color: white;
              padding: 3px 8px;
              border-radius: 3px;
              display: inline-block;
              font-size: 11px;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              font-size: 11px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 10px;
            }
            .print-date {
              text-align: right;
              font-size: 11px;
              color: #666;
              margin-bottom: 10px;
            }
            .amount {
              font-weight: bold;
            }
            @media print {
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-date">Printed on: ${currentDate}</div>
          
          <div class="header">
            <h2>Advance Payment List</h2>
            <p>Industrial Park Development Corporation</p>
          </div>
          
          <div class="filter-info">
            <table>
              <tr>
                <td><strong>Date Range:</strong> ${fromDateFormatted} to ${toDateFormatted}</td>
                <td><strong>Status Filter:</strong> ${statusText}</td>
              </tr>
            </table>
          </div>
          
          <div class="summary">
            <table>
              <tr>
                <td class="label">Total Records:</td>
                <td>${filteredPayments.length}</td>
                <td class="label">Paid:</td>
                <td>${paidCount}</td>
              </tr>
              <tr>
                <td class="label">Total Amount:</td>
                <td>${totalAmount}</td>
                <td class="label">Unpaid:</td>
                <td>${unpaidCount}</td>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td class="label">Requested:</td>
                <td>${requestedCount}</td>
              </tr>
            </table>
          </div>
          
          ${tableHtml.outerHTML}
          
          <div class="footer">
            <p>This is a computer generated report. No signature required.</p>
            <p>LIS © ${new Date().getFullYear()} All Rights Reserved</p>
          </div>
        </body>
        </html>
      `);
      
      printWindow.document.close();
      
      // Wait for content to load then print
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      }, 500);
      
      showSuccessToast("Print prepared successfully");
    } catch (error) {
      console.error("Error printing:", error);
      showErrorToast("Failed to print");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agreementId) {
      showErrorToast("Please select an agreement");
      return;
    }
    if (!formData.paymentDate) {
      showErrorToast("Please select payment date");
      return;
    }
    if (!formData.paymentAmount) {
      showErrorToast("Please enter payment amount");
      return;
    }
    if (!formData.paymentAmount || parseFloat(formData.paymentAmount) <= 0) {
      showErrorToast("Please enter a valid payment amount greater than 0");
      return;
    }
    if (!formData.paidThrough) {
      showErrorToast("Please select paid through option");
      return;
    }
    if (isBankFieldEnabled() && !formData.bankName) {
      showErrorToast("Please enter bank name");
      return;
    }

    setIsLoading(true);
    showInfoToast("Submitting advance payment...");

    try {
      console.log("Submitting form data:", formData);
      const result = await ExecutePostWithParams("finances/advancepayment", formData);
      
      if (result) {
        showSuccessToast("Advance payment information successfully saved!");
        resetForm();
      } else {
        showErrorToast("Failed to save advance payment");
      }
    } catch (err) {
      console.error("Error saving payment:", err);
      showErrorToast(err?.message || "Saving payment was unsuccessful, please try again!");
    } finally {
      setIsLoading(false);
    }
  };

  // Common font style
  const fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
  
  const labelStyle = {
    fontFamily: fontFamily,
    fontSize: '1.0rem',
    fontWeight: 'normal',
    color: '#212529'
  };

  const inputStyle = {
    fontFamily: fontFamily,
    fontSize: '0.95rem'
  };

  const menuItemStyle = {
    fontFamily: fontFamily,
    fontSize: '0.95rem',
    color: '#000000',
    fontWeight: 'normal'
  };

  return (
    <Box sx={{ 
      maxWidth: '1200px', 
      margin: '0 auto',
      fontFamily: fontFamily,
      mt: 5
    }}>
      {/* Toast Notification */}
      <Snackbar 
        open={toastOpen} 
        autoHideDuration={6000} 
        onClose={handleToastClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleToastClose} 
          severity={toastSeverity} 
          sx={{ 
            width: '100%',
            fontSize: '0.95rem',
            fontWeight: 500
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>

      <Paper elevation={1} sx={{ p: 4, borderRadius: 1 }}>
        {/* Tabs with spacing */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="advance payment tabs">
            <Tab 
              label="Advance Payment Registration" 
              sx={{ 
                mr: 2,
                fontFamily: fontFamily,
                fontSize: '0.95rem',
                fontWeight: 500
              }} 
            />
            <Tab 
              label="Advance Payment List" 
              sx={{ 
                fontFamily: fontFamily,
                fontSize: '0.95rem',
                fontWeight: 500
              }} 
            />
          </Tabs>
        </Box>

        {/* Tab 1: Advance Payment Registration */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="h6" sx={{ 
            fontFamily: fontFamily,
            fontSize: '1.0rem',
            mb: 3,
            pb: 1,
            borderBottom: '1px solid #e0e0e0'
          }}>
            Advance Payment Registration
          </Typography>

          {/* Agreement Type Selector */}
          <Box sx={{ mb: 3, px: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel sx={labelStyle}>Agreement Type</InputLabel>
                  <Select
                    value={selectedAgreementType}
                    onChange={handleAgreementTypeChange}
                    label="Agreement Type"
                    size="small"
                    sx={inputStyle}
                    disabled={isLoadingAgreements}
                  >
                    {agreementTypes.map(type => (
                      <MenuItem key={type.id} value={type.id} style={menuItemStyle}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {isLoadingAgreements && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2" color="textSecondary">
                      Loading agreements...
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
            {selectedAgreementType !== "all" && filteredAgreements.length > 0 && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Showing {filteredAgreements.length} agreement(s) of type "{agreementTypes.find(t => t.id === selectedAgreementType)?.label || selectedAgreementType}"
              </Typography>
            )}
          </Box>

          <form onSubmit={handleSubmit}>
            <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
              <div className="row gx-5 gy-4">
                {/* Agreement ID */}
                <div className="col-md-6">
                  <TextField
                    select
                    fullWidth
                    name="agreementId"
                    label="Agreement ID"
                    value={formData.agreementId}
                    onChange={handleChange}
                    size="small"
                    variant="outlined"
                    required
                    InputLabelProps={{ style: labelStyle }}
                    inputProps={{ style: inputStyle }}
                    disabled={isLoadingAgreements || filteredAgreements.length === 0}
                    sx={{
                      width: '85%',
                      marginBottom: '20px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                      '& .MuiSelect-select': {
                        color: '#000000',
                      }
                    }}
                  >
                    <MenuItem value="" style={menuItemStyle}>Select Agreement</MenuItem>
                    {filteredAgreements && filteredAgreements.length > 0 ? (
                      filteredAgreements.map((agreement) => {
                        const agreementType = agreement.agreement_type || agreement.ownership_type || '';
                        const paymentInfo = agreement.advance_payment && parseFloat(agreement.advance_payment) > 0 ? 
                          ` (Advance: ${agreement.advance_payment})` : 
                          agreement.down_payment && parseFloat(agreement.down_payment) > 0 ?
                          ` (Down: ${agreement.down_payment})` :
                          agreement.security && parseFloat(agreement.security) > 0 ? 
                          ` (Security: ${agreement.security})` : '';
                        
                        return (
                          <MenuItem 
                            key={agreement.id || agreement.agreement_id} 
                            value={agreement.id || agreement.agreement_id} 
                            style={menuItemStyle}
                          >
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 1,
                              fontFamily: fontFamily,
                              fontSize: '0.95rem',
                              color: '#000000',
                              fontWeight: 'normal'
                            }}>
                              {agreement.agreement_id || agreement.agreementId || agreement.name || agreement.id}
                              {paymentInfo}
                            </Box>
                          </MenuItem>
                        );
                      })
                    ) : (
                      <MenuItem disabled value="" style={menuItemStyle}>
                        {selectedAgreementType !== "all" 
                          ? `No ${agreementTypes.find(t => t.id === selectedAgreementType)?.label || selectedAgreementType} agreements available` 
                          : "No agreements available"}
                      </MenuItem>
                    )}
                  </TextField>
                  {filteredAgreements.length === 0 && !isLoadingAgreements && (
                    <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                      No agreements found for the selected type
                    </Typography>
                  )}
                </div>

                {/* Payment Date */}
                <div className="col-md-6">
                  <TextField
                    fullWidth
                    type="date"
                    name="paymentDate"
                    label="Payment Date"
                    value={formData.paymentDate}
                    onChange={handleChange}
                    size="small"
                    variant="outlined"
                    required
                    InputLabelProps={{
                      shrink: true,
                      style: labelStyle
                    }}
                    inputProps={{ style: inputStyle }}
                    sx={{
                      width: '85%',
                      marginBottom: '20px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </div>

                {/* Payment Amount */}
                <div className="col-md-6">
                  <TextField
                    fullWidth
                    type="number"
                    name="paymentAmount"
                    label="Advance Amount"
                    value={formData.paymentAmount}
                    onChange={handleChange}
                    size="small"
                    variant="outlined"
                    disabled
                    InputLabelProps={{ style: labelStyle }}
                    inputProps={{
                      style: inputStyle,
                      min: 0,
                      step: 0.01
                    }}
                    sx={{
                      width: '85%',
                      marginBottom: '20px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </div>

                {/* Payment Mode - READ ONLY */}
                <div className="col-md-6">
                  <TextField
                    fullWidth
                    type="text"
                    name="paymentMode"
                    label="Payment Mode (USD/ETB)"
                    value={formData.paymentMode}
                    size="small"
                    variant="outlined"
                    disabled
                    InputProps={{
                      readOnly: true,
                      style: { ...inputStyle, backgroundColor: '#f5f5f5' }
                    }}
                    InputLabelProps={{ style: labelStyle }}
                    sx={{
                      width: '85%',
                      marginBottom: '20px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </div>

                {/* Paid Through Dropdown */}
                <div className="col-md-6">
                  <FormControl fullWidth required sx={{ width: '85%', marginBottom: '20px' }}>
                    <InputLabel sx={labelStyle}>Paid Through</InputLabel>
                    <Select
                      name="paidThrough"
                      value={formData.paidThrough}
                      onChange={handleChange}
                      label="Paid Through"
                      size="small"
                      sx={inputStyle}
                    >
                      <MenuItem value="" style={menuItemStyle}>Select Payment Method</MenuItem>
                      {paidThroughOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value} style={menuItemStyle}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>

                {/* Bank Name */}
                <div className="col-md-6">
                  <TextField
                    fullWidth
                    type="text"
                    name="bankName"
                    label="Bank Name"
                    value={formData.bankName}
                    onChange={handleChange}
                    size="small"
                    variant="outlined"
                    required={isBankFieldEnabled()}
                    disabled={!isBankFieldEnabled()}
                    InputLabelProps={{ style: labelStyle }}
                    inputProps={{ 
                      style: { 
                        ...inputStyle, 
                        backgroundColor: !isBankFieldEnabled() ? '#f5f5f5' : 'transparent' 
                      } 
                    }}
                    sx={{
                      width: '85%',
                      marginBottom: '20px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </div>

                {/* Reference No */}
                <div className="col-md-6">
                  <TextField
                    fullWidth
                    type="text"
                    name="referenceNo"
                    label="Reference No"
                    value={formData.referenceNo}
                    onChange={handleChange}
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ style: labelStyle }}
                    inputProps={{ style: inputStyle }}
                    sx={{
                      width: '85%',
                      marginBottom: '20px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </div>

                {/* Remarks */}
                <div className="col-md-6">
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="remarks"
                    label="Remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    size="small"
                    variant="outlined"
                    InputLabelProps={{ style: labelStyle }}
                    inputProps={{ style: inputStyle }}
                    sx={{
                      width: '85%',
                      marginBottom: '20px',
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: '#1976d2',
                        },
                      },
                    }}
                  />
                </div> 
              </div>
              
              {/* Submit Button */}
              <Box sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  type="submit" 
                  variant="contained"
                  color="inherit"
                  disabled={isLoading || isLoadingAgreements}
                  sx={{
                    fontFamily: fontFamily,
                    fontSize: '1.0rem',
                    textTransform: 'none',
                    fontWeight: 'normal',
                    padding: '5px 15px',
                    minWidth: '200px',
                    marginRight: '7.5%',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      transform: 'translateY(-2px)'
                    },
                    '&:disabled': {
                      backgroundColor: '#e0e0e0',
                      color: '#9e9e9e'
                    }
                  }}
                >
                  {isLoading ? 'Submitting...' : 'Submit Advance Payment'}
                </Button>
              </Box>
            </Box>
          </form>
        </TabPanel>

        {/* Tab 2: Advance Payment List */}
        <TabPanel value={tabValue} index={1}>
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
    <Typography variant="h6" sx={{ 
      fontFamily: fontFamily,
      fontSize: '1.0rem',
      pb: 1,
      borderBottom: '1px solid #e0e0e0'
    }}>
      Advance Payment List
    </Typography>
    
    {/* Print Button at top right corner */}
    <Button
      variant="outlined"
      color="primary"
      startIcon={<PrintIcon />}
      onClick={handlePrint}
      disabled={filteredPayments.length === 0 || isLoadingPayments}
      sx={{
        fontFamily: fontFamily,
        textTransform: 'none',
        borderColor: '#1976d2',
        color: '#1976d2',
        '&:hover': {
          borderColor: '#1565c0',
          backgroundColor: 'rgba(25, 118, 210, 0.04)',
        },
      }}
    >
      Print List
    </Button>
  </Box>

  {/* Filter Section */}
  <Paper sx={{ p: 3, mb: 3, backgroundColor: '#f9f9f9' }}>
    <Grid container spacing={2} alignItems="center">
      {/* From Date */}
      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          type="date"
          name="fromDate"
          label="From Date"
          value={dateRange.fromDate}
          onChange={handleDateRangeChange}
          size="small"
          variant="outlined"
          InputLabelProps={{
            shrink: true,
            style: labelStyle
          }}
          inputProps={{ style: inputStyle }}
        />
      </Grid>

      {/* To Date */}
      <Grid item xs={12} md={2}>
        <TextField
          fullWidth
          type="date"
          name="toDate"
          label="To Date"
          value={dateRange.toDate}
          onChange={handleDateRangeChange}
          size="small"
          variant="outlined"
          InputLabelProps={{
            shrink: true,
            style: labelStyle
          }}
          inputProps={{ style: inputStyle }}
        />
      </Grid>

      {/* Status Filter */}
      <Grid item xs={12} md={2}>
        <FormControl fullWidth>
          <InputLabel sx={labelStyle}>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status"
            size="small"
            sx={inputStyle}
          >
            {statusOptions.map((option) => (
              <MenuItem key={option.value} value={option.value} style={menuItemStyle}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Search Button */}
      <Grid item xs={12} md={2}>
        <Button
          fullWidth
          variant="contained"
          color="inherit"
          onClick={fetchAdvancePayments}
          disabled={isLoadingPayments}
          startIcon={<SearchIcon />}
          sx={{
            fontFamily: fontFamily,
            textTransform: 'none',
            height: '40px'
          }}
        >
          {isLoadingPayments ? 'Searching...' : 'Search'}
        </Button>
      </Grid>

      {/* Clear Button */}
      <Grid item xs={12} md={2}>
        <Button
          fullWidth
          variant="outlined"
          onClick={clearFilters}
          disabled={isLoadingPayments}
          sx={{
            fontFamily: fontFamily,
            textTransform: 'none',
            height: '40px',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          Clear Filters
        </Button>
      </Grid>

      {/* Refresh Button */}
      <Grid item xs={12} md={2}>
        <Button
          fullWidth
          variant="outlined"
          onClick={fetchAdvancePayments}
          disabled={isLoadingPayments}
          startIcon={<RefreshIcon />}
          sx={{
            fontFamily: fontFamily,
            textTransform: 'none',
            height: '40px',
            borderColor: '#1976d2',
            color: '#1976d2',
            '&:hover': {
              borderColor: '#1565c0',
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          Refresh
        </Button>
      </Grid>
    </Grid>
  </Paper>

  {/* Results Table with ref for printing */}
  <Paper sx={{ width: '100%', overflow: 'hidden' }} ref={tableRef}>
    <TableContainer sx={{ maxHeight: 500 }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>S.No</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Agreement ID</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Payment Date</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Payment Mode</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Payment Method</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Bank Name</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Reference No</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoadingPayments ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                <CircularProgress size={30} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Loading advance payments...
                </Typography>
              </TableCell>
            </TableRow>
          ) : filteredPayments.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                <ReceiptIcon sx={{ fontSize: 40, color: '#ccc', mb: 1 }} />
                <Typography variant="body1" color="textSecondary">
                  No advance payments found
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Please adjust your filters and try again
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredPayments
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((payment, index) => {
                // Determine row background color based on status - NO COLOR FOR PAID
                const isRequested = payment.status === "Requested" || payment.display_status === "Pending";
                const isUnpaid = payment.display_status === "Unpaid" || payment.status === "Unpaid";
                // isPaid variable is defined but not used for background color
                
                const displayStatus = payment.status === "Approved" ? "Paid" : 
                                     payment.status === "Requested" ? "Pending" : 
                                     payment.display_status || "Unpaid";
                
                return (
                  <TableRow 
                    key={payment.id || index} 
                    hover
                    sx={{
                      // Only apply background colors for non-paid statuses
                      backgroundColor: isRequested ? '#fff3e0' : 
                                       isUnpaid ? '#ffebee' : 
                                       'inherit', // Paid records get no background color
                      '&:hover': {
                        backgroundColor: isRequested ? '#ffe0b2' : 
                                        isUnpaid ? '#ffcdd2' : 
                                        'rgba(0, 0, 0, 0.04)', // Default hover for paid
                      }
                    }}
                  >
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" >
                        {payment.agreement_id || payment.agreementId || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(payment.payment_date)}</TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#000' }}>
                        {formatCurrency(payment.payment_amount, payment.payment_mode)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {payment.payment_mode?.toUpperCase() || '-'}
                    </TableCell>
                    <TableCell>{payment.paid_through || payment.payment_method || '-'}</TableCell>
                    <TableCell>{payment.bank_name || '-'}</TableCell>
                    <TableCell>{payment.reference_no || payment.reference || '-'}</TableCell>
                    <TableCell>
                      {displayStatus}
                    </TableCell>
                  </TableRow>
                );
              })
          )}
        </TableBody>
      </Table>
    </TableContainer>
    
    {/* Pagination */}
    {filteredPayments.length > 0 && (
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={totalRecords}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    )}
  </Paper>

  {/* Enhanced Summary Section with USD and Birr separation */}
  {filteredPayments.length > 0 && (
    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
      <Paper sx={{ p: 2, backgroundColor: '#f0f7ff', minWidth: '350px' }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: '#1976d2', borderBottom: '1px solid #b0c4de', pb: 1 }}>
          Summary
        </Typography>
        
        {/* Total Records */}
        <Grid container spacing={1} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Total Records:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{totalRecords}</Typography>
          </Grid>
        </Grid>
        
        {/* USD Section */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2', display: 'block', mb: 1 }}>
          USD Payments
        </Typography>
        <Grid container spacing={1} sx={{ mb: 1, pl: 1 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Count:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {filteredPayments.filter(p => p.payment_mode?.toUpperCase() === 'USD').length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Total Amount:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              ${filteredPayments
                .filter(p => p.payment_mode?.toUpperCase() === 'USD')
                .reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0)
                .toFixed(2)}
            </Typography>
          </Grid>
        </Grid>
        
        {/* Birr Section */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#1976d2', display: 'block', mb: 1, mt: 1 }}>
          Birr Payments
        </Typography>
        <Grid container spacing={1} sx={{ mb: 2, pl: 1 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Count:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {filteredPayments.filter(p => p.payment_mode?.toUpperCase() === 'BIRR' || p.payment_mode?.toUpperCase() === 'ETB').length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Total Amount:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
              {filteredPayments
                .filter(p => p.payment_mode?.toUpperCase() === 'BIRR' || p.payment_mode?.toUpperCase() === 'ETB')
                .reduce((sum, p) => sum + (parseFloat(p.payment_amount) || 0), 0)
                .toFixed(2)} ETB
            </Typography>
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 1.5 }} />
        
        {/* Status Breakdown */}
        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', display: 'block', mb: 1 }}>
          Status Breakdown
        </Typography>
        <Grid container spacing={1} sx={{ pl: 1 }}>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Paid:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
              {filteredPayments.filter(p => p.status === 'Approved' || p.display_status === 'Paid').length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Pending:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
              {filteredPayments.filter(p => p.status === 'Requested' || p.display_status === 'Pending').length}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Unpaid:</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f44336' }}>
              {filteredPayments.filter(p => p.display_status === 'Unpaid' || p.status === 'Unpaid').length}
            </Typography>
          </Grid>
        </Grid>
        
        {/* Note */}
        <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#999', fontStyle: 'italic' }}>
          Note: USD and Birr amounts are shown separately. Exchange rate not applied.
        </Typography>
      </Paper>
    </Box>
  )}
</TabPanel>
      </Paper>
    </Box>
  );
};

export default AdvancePaymentForm;